import {Component, Inject, OnInit}                                    from '@angular/core';
import {UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators} from '@angular/forms';
import {MAT_DIALOG_DATA, MatDialogRef}                                from '@angular/material/dialog';
import {MatSnackBar}                                                  from '@angular/material/snack-bar';
import {FormCategoryService}                                          from '../../services/form-category.service';
import {FormCategory}                                                 from '../../models/form-category.model';

@Component({
    standalone: false,
    selector: 'app-form-category-form',
    templateUrl: './form-category-form.component.html',
})
export class FormCategoryFormComponent implements OnInit
{
    public title = 'Create Category';
    public category: FormCategory | null = null;

    public categoryForm: UntypedFormGroup = new UntypedFormGroup({});

    public constructor(
        @Inject(MAT_DIALOG_DATA) public data: { category: FormCategory | null },
        private formCategoryService: FormCategoryService,
        private formBuilder: UntypedFormBuilder,
        private snackBar: MatSnackBar,
        public dialogRef: MatDialogRef<FormCategoryFormComponent>,
    )
    {
    }

    public ngOnInit(): void
    {
        this.category = this.data.category;

        if (this.category) {
            this.title = this.category.is_system
                ? `Edit Settings — ${this.category.name}`
                : 'Edit Category';
        }

        this.categoryForm = this.formBuilder.group({
            name: new UntypedFormControl(
                {value: this.category?.name ?? '', disabled: this.category?.is_system ?? false},
                Validators.required
            ),
            kind: new UntypedFormControl({
                value: this.category?.kind ?? 'work',
                disabled: this.category?.is_system ?? false,
            }),
            sort_order: new UntypedFormControl({
                value: this.category?.sort_order ?? 0,
                disabled: this.category?.is_system ?? false,
            }),
            settings: this.formBuilder.array(
                Object.entries(this.category?.settings ?? {}).map(([key, value]) =>
                    this.buildSettingRow(key, value)
                )
            ),
        });
    }

    public get settingsRows(): UntypedFormArray
    {
        return this.categoryForm.get('settings') as UntypedFormArray;
    }

    public addSettingRow(): void
    {
        this.settingsRows.push(this.buildSettingRow('', ''));
    }

    public removeSettingRow(index: number): void
    {
        this.settingsRows.removeAt(index);
    }

    public submitForm(): void
    {
        if (this.categoryForm.invalid) {
            this.categoryForm.markAllAsTouched();
            return;
        }

        const payload: any = {
            settings: this.settingsToObject(),
        };

        if (!this.category?.is_system) {
            payload.name = this.categoryForm.get('name')?.value;
            payload.kind = this.categoryForm.get('kind')?.value;
            payload.sort_order = Number(this.categoryForm.get('sort_order')?.value ?? 0);
        }

        const request = this.category
            ? this.formCategoryService.update(this.category.id, payload)
            : this.formCategoryService.create(payload);

        request.subscribe({
            next: response => this.dialogRef.close(response),
            error: response => {
                const message = response?.error?.message ?? 'Failed to save category.';
                this.snackBar.open(message, 'Ok', {duration: 4000});
            }
        });
    }

    public closeDialog(): void
    {
        this.dialogRef.close();
    }

    private buildSettingRow(key: string, value: unknown): UntypedFormGroup
    {
        return this.formBuilder.group({
            key: new UntypedFormControl(key, Validators.required),
            value: new UntypedFormControl(typeof value === 'string' ? value : JSON.stringify(value)),
        });
    }

    /**
     * Values round-trip through JSON where possible so structured settings
     * (e.g. required_system_fields: ["door_type"]) survive the key/value
     * editor; anything unparsable is kept as a plain string.
     */
    private settingsToObject(): Record<string, unknown> | null
    {
        const entries = (this.settingsRows.value as Array<{ key: string; value: string }>)
            .filter(row => (row.key ?? '').trim() !== '')
            .map(row => {
                const raw = row.value ?? '';
                try {
                    return [row.key.trim(), JSON.parse(raw)];
                } catch {
                    return [row.key.trim(), raw];
                }
            });

        return entries.length ? Object.fromEntries(entries) : null;
    }
}
